<template>
  <div id="app" :class="{ 'wcag-mode-on': !isNormalMode }">
    <v-notification
      v-if="isAvailableNotification(notification.maintenance)"
      class="infomation"
      :notification="notification.maintenance"
    >
      <p>
        メンテナンス実施のため、以下の日程でサービスをご利用いただけなくなります。
        <strong>2022/7/21 2:00~5:00 (予定)</strong>
        ご不便をおかけいたしますが、何卒ご理解いただきますようお願い申し上げます。
      </p>
    </v-notification>

    <v-notification v-else-if="isIE11" class="important" :notification="notification.ie11">
      <p>
        バカンではInternet Explorer11のサポートを2022年4月末にて終了しております詳細は<a
          target="_blank"
          rel="noopener noreferrer"
          href="https://support.vacancorp.com/hc/ja/articles/4412625463833"
          >こちら</a
        >をご確認ください
      </p>
    </v-notification>

    <wcag-dialog
      v-if="isWcagDialogShown"
      :action-title="$t('userList.colorContrast.header')"
      max-width="436px"
      min-height="305px"
      :p-normal-mode="isNormalMode"
      @hide="setWcagDialogShown(false)"
      @ok="setWcagContract"
    />

    <nudge-message
      v-if="isContrastNudgeShown"
      status="wcag"
      :message="$t('userList.colorContrast.nudgeTitle')"
      :timeout="99999"
      width="400px"
      position="bottom"
      @clicked="clickContrastNudge"
      @close="closeContrastNudge"
    />

    <router-view />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import ie from "@/utils/ie11";
import isBetween from "dayjs/plugin/isBetween";
import { envShortName } from "@/lib/env-helper";
import { setCookie, getCookie } from "@/lib/cookie";
import { isWcagMode } from "@/lib/wcag";
import dayjs from "dayjs";
dayjs.extend(isBetween);

export type Notification = { name: string; startDate: string; endDate: string };

export default Vue.extend({
  name: "app",

  metaInfo() {
    return {
      // global template
      titleTemplate:
        process.env.VUE_APP_MODE === "production"
          ? `%s | VACAN`
          : `[${envShortName(process.env.VUE_APP_MODE ?? "")}]` + `%s | VACAN`,
    };
  },

  components: {
    VNotification: () => import("@/components/v/notification.vue"),
    WcagDialog: () => import("@/components/vacan-components/organisms/wcag-mode-modal.vue"),
    NudgeMessage: () => import("@/components/vacan-components/molecules/nudge-message.vue"),
  },

  data() {
    return {
      notification: {
        ie11: { name: "ie", startDate: "2022-02-09T00:00:00+09:00", endDate: "2022-11-01T00:00:00+09:00" },
        // maintenance: { name: "maintenance", startDate: "2022-07-05T00:00:00", endDate: "2022-07-21T05:00:00" },
        maintenance: {
          name: "maintenance",
          startDate: "2022-07-01T00:00:00+09:00",
          endDate: "2022-07-21T02:00:00+09:00",
        },
      },
      isNormalMode: true,
      isContrastNudgeShown: true,
    };
  },

  computed: {
    ...mapState("menu", ["isWcagDialogShown"]),
    isIE11(): boolean {
      return ie.isIE11();
    },
  },

  mounted() {
    this.isNormalMode = !isWcagMode();
    this.isContrastNudgeShown = getCookie("color-mode") === undefined;
  },

  methods: {
    ...mapMutations("menu", ["setWcagDialogShown"]),

    isAvailableNotification(notification: Notification): boolean {
      const now = dayjs();
      return now.isBetween(notification.startDate, notification.endDate);
    },

    setWcagContract(isNormalMode: boolean) {
      this.setWcagDialogShown(false);
      this.setColorMode(isNormalMode);
    },

    setColorMode(isNormalMode: boolean) {
      if (!isNormalMode) {
        document.body.classList.add("wcag-mode-on");
      } else {
        document.body.classList.remove("wcag-mode-on");
      }

      this.isNormalMode = isNormalMode;
      setCookie("color-mode", isNormalMode ? "normal" : "wcag", 400);
    },

    closeContrastNudge() {
      this.isContrastNudgeShown = false;
    },

    clickContrastNudge() {
      this.isContrastNudgeShown = false;
      this.setWcagDialogShown(true);
    },
  },
});
</script>

<style lang="scss">
@import "~@vacancorp/vacan-scss/vacan.scss";
@import "@/assets/css/material-icons.scss";

input,
button,
select,
textarea {
  appearance: none;
}

html,
body {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  overscroll-behavior: none;
  overflow: hidden;
  color: $text; // overwrite gray-4 on vacan.scss
}

#app {
  height: 100%;
  width: 100%;
}

fieldset {
  padding: 0;
  margin: 0;
  border: none;
}

/** dynamic colors for wcag mode of web a11y */
:root {
  --spooky-ghost: #{$spooky-ghost};
  --lime: #{$lime};
  --mint: #{$mint};
  --important: #{$important};
  --orange: #{$orange};
  --tiger: #{$tiger};
  --cheek: #{$cheek};
  --coral: #{$coral};
  --link: #{$link};
}
#app.wcag-mode-on {
  --spooky-ghost: #{$wcag_spooky-ghost};
  --lime: #{$wcag_green};
  --mint: #{$wcag_green};
  --important: #{$wcag_important};
  --orange: #{$wcag_yellow};
  --tiger: #{$wcag_yellow};
  --cheek: #{$wcag_red};
  --coral: #{$wcag_red};
  --link: #{$wcag_link};
}
</style>
